import { Loader2 } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { toast } from 'sonner';

import { useUpdateStudentInInvoice } from '~entities/parent/api/update-student-in-invoice';
import { useGetStudentsRelationsByParentId } from '~entities/student/api/get-student-relations-by-studentId';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

interface AddStudentProps {
  parentId: string;
  invoiceId: string;
  studentId: string;
}

export const AddStudent = forwardRef<HTMLButtonElement, AddStudentProps>(
  ({ parentId, invoiceId, studentId }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedStudentId, setSelectedStudentId] = useState<
      string | undefined
    >(undefined);
    const { data, isLoading } = useGetStudentsRelationsByParentId(parentId);
    const { mutateAsync: updateStudentInInvoice, isPending } =
      useUpdateStudentInInvoice();

    const handleAddStudent = async () => {
      if (!selectedStudentId) return;
      try {
        await updateStudentInInvoice({
          invoiceId,
          studentId: selectedStudentId,
          parentId,
        });
        toast.success('Student added to invoice successfully');
        setIsOpen(false);
      } catch (error) {
        console.error(error);
        toast.error('Failed to add student to invoice');
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant='outline' ref={ref}>
            Add Student to Invoice
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Student to Invoice</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            Add a student to the invoice. This will update the invoice with the
            new student.
          </DialogDescription>
          {isLoading ? (
            <div className='flex items-center justify-center'>
              <Loader2 className='size-4 animate-spin' />
            </div>
          ) : (
            <div className='flex flex-col gap-2'>
              <Select
                value={selectedStudentId ?? ''}
                onValueChange={(value) => {
                  setSelectedStudentId(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select student' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {data?.map((student) => (
                      <SelectItem key={student._id} value={student._id}>
                        {student.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {selectedStudentId === studentId && (
                <p className='text-sm text-red-500'>
                  Student already added to invoice.
                </p>
              )}
            </div>
          )}
          <DialogFooter className='mt-20'>
            <DialogClose asChild>
              <Button variant='outline' disabled={isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              disabled={
                !selectedStudentId ||
                isPending ||
                selectedStudentId === studentId
              }
              onClick={handleAddStudent}
            >
              {isPending && <Loader2 className='size-4 animate-spin' />}
              Add Student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

AddStudent.displayName = 'AddStudent';
